import {
    queue,
    series,
    deepClone,
    deepMixin,
    isObject,
    isFunction,
    isNumber,
    isArray
} from "../../funcs";
import Backbone from "backbone";
import RowsStorage from "./RowsStorage";
import RowModel from "./RowModel";
import Filter from "../api/filter/Filter";
import Filter3 from "./filter3/Filter3";


/**
         * Cursor это Backbone коллекция,
         * умеющая подгружать строки с помощью RowsStorage.
         * Cursor подгружает 1000(size) строк, но в models хранит только один кадр (limit)
         * @class Cursor
         * @extends Backbone.Collection
         * @param {Array} arr
         * @param {Object} params Объект вида:
         * {
         *   name: {String} Имя таблицы
         * }
         *
         */
const Cursor = Backbone.Collection.extend("Cursor", {
    model: RowModel,

    // кол-во строк в памяти
    size: 120,
    // по этому параметру, grid будет определять,
    // что коллекция асинхронная и содержит типичный функционал Cursor'а
    isCursor: true,

    initialize(arr, params) {
        this.name = params.name;
        // id строки, за которой следим
        this.lookId = false;
        // столбцы, которые подгружаем
        this.columns = {};
        // сортировка строк
        this._sort = null;
        // фильтры строк
        this.filter = null;
        // признак того, что нужно
        this._refresh = true;
        // очередь помогает обрабатывать частое обращение к методу load
        this._queue = queue(this);
        // индекс первой строки, доступной в кэше
        this.firstRowIndex = 0;
        // индекс первой строки, которую видит пользователь
        this.offset = 0;
        // кол-во строк, которое видит пользователь
        this.limit = 1;
        // если передали params.cache = false
        // то всегда обращаемся к серверу,
        // этот параметр нужен, когда есть подзапрос в entity,
        // и сложно следить за актуальностью данных подзапроса
        this._noCache = params.cache === false;
        // кэш
        this.initStorage(params);

        // список строк, доступных в кэше
        this.ids = [];
        // кол-во строк в таблице при определенных фильтрах
        this.count = 0;

        // прикрипленные строки
        // новые места моделей index => {id, ignoreFilter}
        this.attached = {};

        this.scope = false;
    },

    initStorage(params) {
        this.storage = RowsStorage.get(params.name, params.DELETED);

        // Ад и Израиль

        if ( params.DELETED === 1 ) {
            this.storage.on("untrash",(models) => {
                models.forEach((model) => {
                    this._onRemove(model);
                });
            });
            this.storage.on("trash", (models) => {
                models.forEach((model) => {
                    this._onCreate(model);
                });
            });
        } else {
            this.storage.on("trash __customRefreshExclude",(models) => {
                models.forEach((model) => {
                    this._onRemove(model);
                });
            });
            this.storage.on("untrash", (models) => {
                models.forEach((model) => {
                    this._onCreate(model);
                });
            });

            this.storage.on("create socketCreate", (model) => {
                this._onCreate(model);
            });
        }

        this.storage.on("remove",(models) => {
            models.forEach((model) => {
                this._onRemove(model);
            });
        });

        // модель может попасть в фильтр,
        // а может уйти из него
        this.storage.on("socketUpdate update", (model) => {
            this._onUpdate(model);
        });

        this.storage.on("__customRefreshInclude", (model) => {
            this._onCreate(model);
        });

        // данные поменялись, нужно сделать настоящий запрос
        this.onLiveReloadStorage = () => {
            this._refresh = true;
        };

        this.storage.on("liveReload", this.onLiveReloadStorage);
    },

    _onCreate(model) {
        // Созданная строка не подходит под фильтры
        let inFilter = this._filterModel(model);
        let rightsFilter = this.storage.getRightsAccessFilter();
        let inRights = rightsFilter ? rightsFilter.check(model) : true;

        if ( !inFilter || !inRights ) {
            return;
        }

        // защита от повторного вызова
        if ( this.ids.includes(model.id + "") ) {
            return;
        }

        // следующий load, должен обновить ids
        this._refresh = true;

        this.trigger("beforeAddModel", model);

        this.ids.push(model.id + "");

        if ( this.models.length < this.limit ) {
            this.add(model);
            this.trigger("addModels", [model]);

            // render grid
            this.trigger("updateRows");
        }
    },

    _onUpdate(model) {
        let inFilterNow = this._filterModel(model);
        let inFilterOld = this._hasId(model.id);

        // модель не подходит под фильтры
        // и она не попадает в кадр
        if ( !inFilterNow && !inFilterOld ) {
            return;
        }

        // Модели не было в кадре,
        // но сейчас она подходит по фильтрам
        if ( inFilterNow && !inFilterOld ) {
            this._onCreate(model);
            return;
        }

        // Модель была в кадре,
        // но теперь НЕ подходит по фильтры
        if ( !inFilterNow && inFilterOld ) {
            this._onRemove(model);
            return;
        }
    },

    _onRemove(model) {
        // Удаленная строка не подходит под фильтры
        let inFilter = this._filterModel(model);
        if ( !inFilter ) {
            return;
        }

        // эта модель могла быть закреплена
        for (let index in this.attached) {
            if ( this.attached[ index ].id == model.id ) {
                delete this.attached[ index ];
            }
        }

        // от нее мог считаться offset
        if ( this.lookId == model.id ) {
            this.lookId = false;
        }

        // порядок строк
        let idIndex = this.ids.indexOf(model.id + "");
        if ( idIndex != -1 ) {
            this.ids.splice(idIndex, 1);
            // т.к. this.ids стал меньше,
            // то он не актуален,
            // и следующая загрузка данных
            // его поправит
            this._refresh = true;
        }

        // если строка попадала в кадр (this.models)
        // то надо достать из storage новую модель,
        // и вставить в кадр
        let modelIndex = -1;
        for (let i = 0, n = this.models.length; i < n; i++) {
            let knownModel = this.models[i];

            if ( knownModel.id == model.id ) {
                modelIndex = i;
                break;
            }
        }

        if ( modelIndex == -1 ) {
            return;
        }
        this.remove(model.id);
        this.count--;

        let lastModel = this.models.slice(-1)[0];
        if ( lastModel ) {
            let nextIndex = this.ids.findIndex((id) =>
                id == lastModel.id
            );
            let nextId = this.ids[ nextIndex + 1 ];
            let nextModel = this.storage.collection.get(nextId);

            // удалось из кэша достать замену, в кадр
            // просто сообщим гриду нарисовать новое состояние
            if ( nextModel ) {
                this.add(nextModel);
                // sync ItemsCollection
                this.trigger("addModels", [nextModel]);

                // render grid
                this.trigger("updateRows");
            }
            // кэш незнает про новую строку, или ее нет
            else if ( this.count > this.models.length ) {
                this._refresh = true;
                this.load();
            }
            else {
                // render grid
                this.trigger("updateRows");
            }
        }
        // была удалена последняя модель
        else {
            // render grid
            this.trigger("updateRows");
        }
    },

    _filterModel(model) {
        let params = this.getParams();

        if ( params.filter ) {
            return params.filter.check(model);
        }

        if ( params.filter3 ) {
            let filter3 = params.filter3;
            if ( !(filter3 instanceof Filter3) ) {
                filter3 = new Filter3(filter3);
            }

            return filter3.check(model.attributes);
        }

        return true;
    },

    // захватывает ли курсор эту модель
    _hasId(id) {
        for (let i = 0, n = this.ids.length; i < n; i++) {
            if ( this.ids[i] == id ) {
                return true;
            }
        }
        return false;
    },

    /**
             * Следить за строкой
             * @param  {Number | String} id строки
             * @memberof Cursor.prototype
             */
    look(id) {
        this.lookId = id;
    },

    _getLookOffset(id) {
        let lookOffset = Math.floor(-this.limit / 2);
        // cursor.offset = %rows%.indexOf(id) + lookOffset
        // смещение относительно наблюдаемой (выделлной) строки,
        // передавать отрицательным!
        this.each(function(model, i) {
            if ( model.id == id ) {
                lookOffset = -i;
            }
        });

        return lookOffset;
    },

    /**
             * Перестать следить за строкой
             * @memberof Cursor.prototype
             */
    loose() {
        this.lookId = false;
    },

    /**
             * Курсор при запросе к кэшу передает столбцы,
             * которые видет пользователь.
             * addColumn добавляет еще один столбец
             * @param  {String} key ключ столбца
             * @memberof Cursor.prototype
             */
    addColumn(key) {
        if ( !this.columns[key] ) {
            this._refresh = true;
        }
        this.columns[key] = true;
    },

    /**
             * Уменьшаем кол-во стобцов, которые видит пользователь
             * @param  {String} key ключ столбца
             * @memberof Cursor.prototype
             */
    removeColumn(key) {
        if ( this.columns[key] ) {
            this._refresh = true;
        }
        delete this.columns[key];
    },

    /**
             * Задаем точное кол-во стобцов, предыдущие столбцы будут заменены
             * @param  {Array} columns Массив ключей
             * @memberof Cursor.prototype
             */
    setColumns(columns) {
        let key;
        // преобразую массив строк в объект, для более удобного сравнения
        let newObj = {};
        for (let i = 0, n = columns.length; i < n; i++) {
            key = columns[i];
            newObj[key] = true;

            // проверяю появились ли новые столбцы
            if ( !this.columns[key] ) {
                this._refresh = true;
            }
        }

        // проверяю не удалились ли старые столбцы
        for (key in this.columns) {
            if ( !newObj[key] ) {
                this._refresh = true;
                break;
            }
        }

        this.columns = newObj;
    },

    /**
             * Задаем фильтр для поиска,
             * при следующем .load будут обновлены строки в буфере
             * @param  {Filter | Conditions} filter @see classes/api/filter/Filter
             * @memberof Cursor.prototype
             */
    setFilter(filter) {
        if ( isObject(filter) && !(filter instanceof Filter) ) {
            filter = new Filter(filter);
        }

        this.filter = filter;
        this._refresh = true;
    },

    // TODO: УДАЛИТЬ ЭТОТ МЕТОД НА ФИГ,
    // использовать только в самых сложных ситуациях
    setFilter3(filter3) {
        this.filter3 = filter3 instanceof Filter3 ? filter3 : new Filter3(filter3);
        this._refresh = true;
    },

    /**
             * Добавляем фильтр
             * @param  {Filter} filter фильтр, которой будет добавлен по AND
             * @memberof Cursor.prototype
             */
    addFilter(filter) {
        if ( !filter ) {
            return;
        }

        if ( !this.filter ) {
            if ( !(filter instanceof Filter) ) {
                filter = new Filter(["AND"].concat(Object.keys(filter)), filter);
            }
            this.filter = filter;
        } else {
            this.filter.add(filter);
        }
        this._refresh = true;
    },

    /**
             * Устанавливаем системный фильтр,
         * который перед запросом будет объединяться по AND с основным фильтров,
             * суть в том, чтобы пользователь не смог с помощью columnFilter
             * сбросить системный фильтр
             * @param {Filter | Conditions} sysFilter системный фильтр, который не конфликтует с другими
             * @memberof Cursor.prototype
             */
    setSysFilter(sysFilter) {
        if ( isObject(sysFilter) && !(sysFilter instanceof Filter) ) {
            sysFilter = new Filter(sysFilter);
        }
        this.sysFilter = sysFilter;
        this._refresh = true;
    },

    setSysTabFilter(sysFilter) {
        const keysFilter = [];

        if ( isObject(sysFilter) && !(sysFilter instanceof Filter) ) {
            sysFilter = new Filter(sysFilter);
        }

        if ( sysFilter ) {
            for ( let key in sysFilter.conditions ) {
                keysFilter.push(key);
            }
        }

        if ( !this.sysFilter ) {
            this.sysFilter = sysFilter;

            if ( this.sysFilter ) {
                this.sysFilter.systemTabsFilters = [...keysFilter];
            }

            this._refresh = true;
        } else {
            if ( this.sysFilter.systemTabsFilters ) {
                this.sysFilter.systemTabsFilters.map((key) => {
                    this.sysFilter.remove(key);
                });
            }

            if ( !sysFilter ) {
                return;
            }

            this.addSysFilter(sysFilter.conditions);
            this.sysFilter.systemTabsFilters = [...keysFilter];
            this._refresh = true;
        }
    },

    /**
             * Добавляем системный фильтр
             * @param  {Filter} sysFilter фильтр, которой будет добавлен по AND
             * @memberof Cursor.prototype
             */
    addSysFilter(sysFilter) {
        if ( !sysFilter ) {
            return;
        }

        if ( !this.sysFilter ) {
            if ( !(sysFilter instanceof Filter) ) {
                sysFilter = new Filter(sysFilter);
            }
            this.sysFilter = sysFilter;
        } else {
            this.sysFilter.add(sysFilter);
        }
        this._refresh = true;
    },

    /**
             * Удаление фильтра
             * @param  {String} key Ключ от условие, которое нужно удалить
             * @memberof Cursor.prototype
             */
    removeSysFilter(key) {
        if ( !this.sysFilter ) {
            return;
        }

        this.sysFilter.remove(key);
        this._refresh = true;
    },

    /**
             * Удаление фильтра
             * @param  {String} key Ключ от условие, которое нужно удалить
             * @memberof Cursor.prototype
             */
    removeFilter(key) {
        if ( !this.filter ) {
            return;
        }

        this.filter.remove(key);
        this._refresh = true;
    },

    /**
             * Поиск фразы по всем колонкам, без учета регистра
             * @param {String} search Поисковая фраза
             * @memberof Cursor.prototype
             */
    setSearch(search) {
        this._search = search;
        this._refresh = true;
    },

    setExcludeFromSearch(columns) {
        this._excludeFromSearch = columns;
    },

    /**
             * Устанавливаем сортировку строк.
             * при следующем .load будут обновлены строки в буфере
             * @param  {Array} sort массив объектов вида:
             * {
             *   ID: 1  // ключ столбец, значение - вектор сортировки
             * }
             * @memberof Cursor.prototype
             */
    setSort(sort) {
        this._sort = sort;
        this._refresh = true;
    },

    /**
             * Смещаем положение курсора на определенную строку
             * @param  {Number} offset индекс первой строки, которую видит пользователь
             * @memberof Cursor.prototype
             */
    setOffset(offset) {
        this.offset = Math.max(offset, 0);
    },

    /**
             * Устанавливаем кол-во моделкй, которые будут браться из кэша на отрисовку
             * @param  {Number} limit кол-во строк
             * @memberof Cursor.prototype
             */
    setLimit(limit) {
        this.limit = limit;
    },

    setTreeParentColumn(parentColumn) {
        this.treeParentColumn = parentColumn;
    },

    /**
             * Формируем параметры для TableApi.get запроса
             * @return {Object} объект,
             * который содержит все необходимые данные для .get запроса.
             * Пример:
             * {
             *   offset: 132,
             *   limit: 39,
             *   filter: {Filter},
             *   sort: [{ID: 1}],
             *   columns: ["ID", "INN"],
             *
             *  //  флаг определяющий, что относительно
             *  // прошлого запроса поменялась сортировка или фильтр и
             *  // нужно сбросить буфер
             *   _refresh: true
             * }
             * @memberof Cursor.prototype
             */
    getParams() {
        let params = {
            offset: Math.max(this.offset, 0),
            limit: this.limit
        };

        // TODO: убрать
        if ( this.filter3 ) {
            params.filter3 = this.filter3;
        }

        if ( this.filter ) {
            params.filter = this.filter.clone();
        }
        if ( this.sysFilter ) {
            if ( params.filter ) {
                params.filter.add(this.sysFilter.clone());
            } else {
                params.filter = this.sysFilter.clone();
            }
        }

        if ( params.filter && params.filter.isEmpty() ) {
            delete params.filter;
        }

        if ( this._sort ) {
            params.sort = this._sort;
        }
        if ( this._search ) {
            params.search = this._search;
        }
        if ( this.colors ) {
            params.colors = true;
        }
        if ( this._createFields ) {
            params.createFields = true;
        }

        if ( this._distinct ) {
            params.distinct = this._distinct;
        }

        if ( Object.keys(this.attached).length ) {
            params.attach = deepClone(this.attached);
        }

        if ( isObject(this.scope) ) {
            params.scope = deepClone(this.scope);
        }

        if ( this._excludeFromSearch ) {
            params.excludeFromSearch = this._excludeFromSearch;
        }

        if ( this.treeParentColumn ) {
            params.treeParentColumn = this.treeParentColumn;
        }

        //
        let columns = [];
        for (let key in this.columns) {
            columns.push(key);
        }
        if ( columns.length === 0 ) {
            columns = "all";
        }
        params.columns = columns;
        params._refresh = this._refresh;

        return params;
    },

    getSort() {
        return deepClone(this._sort);
    },

    /**
             * Пользовательский метод load, который ставит загрузку в очередь
             * @param  {Object}   options  необязательный параметр,
             * Объект вида:
             * {
             *   ignore: {Boolean} // Можно ли игнорировать запросы в промежуток загруки
             * }
             * @param  {Function} callback (models) Массив объектов(строк/записей из таблицы),
             * которые подходят под params(условия для поиска)
             * @memberof Cursor.prototype
             */
    load(options, callback) {

        if ( isFunction(options) ) {
            callback = options;
            options = false;
        }
        if ( !isFunction(callback) ) {
            callback = function() {};
        }
        options = deepMixin({
            ignore: false,
            cache: true
        }, options);

        if ( this._noCache ) {
            options.refresh = true;
            options.cache = false;
        }

        let params = this.getParams();
        if ( options.refresh ) {
            params._refresh = true;
        }
        this._refresh = false;
        let load = this._load.bind(this, options, params);

        let qMethod = this._queue.rotation;
        if ( options.ignore ) {
            qMethod = this._queue.ignore;
        }
        qMethod = qMethod.bind(this._queue);

        let oldOffset = this.offset;
        this.trigger("load:start");
        this.isLoading = true;

        qMethod(load, function(models) {
            this.reset([]); // reset() создает пустую модель (@see Backbone.Collection.prototype.reset)
            models.forEach(function(model, i) {
                this.add(model, { at: i });
            }, this);

            if ( oldOffset != this.offset ) {
                this.trigger("changeOffsetByLoad", oldOffset);
            }

            // для обработки данных
            this.trigger("addModels", models);
            // для отрисовки
            this.trigger("updateRows");

            callback.call(this, models);

            this.trigger("load:stop");
            this.isLoading = false;
        });
    },

    /**
             * Системный метод _load определяет что нужно сделать,
             * обновить буфер или взять строки из буфера
             * @param  {Object}   options   параметры, с которыми был вызван .load
             * @param  {Object}   params   параметры для get запроса
             * @param  {Function} callback (models) Массив моделей(строк/записей из таблицы),
             * которые подходят под params(условия для поиска)
             * @memberof Cursor.prototype
             */
    _load(options, params, callback) {
        let _refresh = !!params._refresh;
        delete params._refresh;
        params.cache = options.cache;
        params.rightOff = options.rightOff;

        if ( !isFunction(callback) ) {
            callback = function() {};
        }

        // _refresh === true
        // (изменились фильтры или сортировки или столбцы..)
        //   мы должны зафиксироваться на lookId, если он есть, и
        //   обновить буфер относительно искомой строки (lookId)
        //   если его нет, то просто обновить буфер
        //
        // _refresh === false
        // (скорей всего просто изменился offset,
        // пользователь перемещается по таблице)
        //  мы должны проверить, что искомы offset не выходит за
        //  пределы нащего буфера (1000 строк),
        //  если пользователь выходит за эти пределы,
        //  то дополняем буфер
        //  (храним только id строк, которые выходят за пределы кадра),
        //  если нашего буфера достаточно, то просто берем из хранилища
        //  нужные строки

        // все есть, берем из кэша
        if ( !_refresh ) {
            this._sliceLoad(params, callback);
        }
        // загружаемся по простому offset'у без приблуд,
        // никто НЕ выделял строчку синим курсором (grid.selection)
        else if ( !this.lookId ) {
            let index = params.offset - this.size / 2;
            this._refreshLoad(index, params, callback);
        }
        else {
            this._lookIdLoad(options, params, callback);
        }
    },

    async _lookIdLoad(options, params, callback) {
        params = deepClone(params);
        params.id = this.lookId;

        let lookOffset = this._getLookOffset(this.lookId);

        let index = await this.storage.indexOf(params);
        delete params.id;

        // строка не найдена,
        // значит перемещаемся в начало списка
        if ( index == -1 ) {
            index = 0;
            params.offset = 0;
        } else {
            if ( isNumber(params.limit) && params.limit > 0 ) {
                // фиксим баг с "прыганием" грида при reload'е
                if ( isNumber(lookOffset) ) {
                    params.offset = index + lookOffset;
                } else {
                    params.offset = index - Math.floor(params.limit / 2);
                }
            } else {
                params.offset = index;
            }
            params.offset = Math.max(params.offset, 0);
            index -= this.size / 2;
        }

        this._refreshLoad(index, params, (models) => {
            callback(models);
            this.trigger("lookIndex", this.lookId);
        });
    },

    /**
             * Обновление данных буфера.
             * @param  {Number}   index    Индекс строки,
             * с которой должен начинаться буфер. Должен быть больше нуля.
             * @param  {Object}   params   типичные .get параметры
             * params.offset и params.limit должны попадать в условие:
             * params.offset + params.limit <= index + this.size &&
             * params.offset >= index
             * иначе будет ошибка
             * @param  {Function} callback
             * callback(models)
             * models - массив моделей из кэша
             * models поподают под условия для поиска (params)
             * @memberof Cursor.prototype
             */
    async _refreshLoad(index, params, callback) {
        let offset = params.offset;
        let limit = params.limit;

        // оптимизация, если сервер вернет моделей меньше чем просили,
        // то это и есть их кол-во
        let matchCountByModels = offset === 0;

        if ( !matchCountByModels && params._loadCount !== false ) {
            this.count = await this.storage.getCount(params);
        }

        let count = this.count;

        if ( matchCountByModels ) {
            index = 0;
        } else if ( index > count - this.size / 2 ) {
            index = Math.floor(count - this.size / 2);
        }

        index = Math.max(index, 0);

        params = deepClone(params);
        params.offset = index;
        params.limit = this.size;

        if ( this.limit == "all" ) {
            params.limit = "all";
        }

        let models = await this.storage.get(params);

        if ( matchCountByModels ) {
            if ( models && models.length < this.size ) {
                this.count = models.length;
            } else {
                this.count = await this.storage.getCount(params);
            }
            count = this.count;
        }

        this.firstRowIndex = index;
        if ( !models ) {
            console.log("waat");
        }

        this.ids = models.map((model) => model.id + "");

        let maxOffset = Math.max(count - limit, 0);
        if ( offset > maxOffset ) {
            offset = maxOffset;
        }

        this.offset = offset;

        if ( limit == "all" ) {
            models = this.ids.slice(offset - index);
        } else {
            models = this.ids.slice(offset - index, offset - index + limit);
        }

        models = models.map((id) =>
            this.storage.collection.get(id)
        );

        if ( callback ) {
            callback(models);
        } else {
            return models;
        }
    },

    /**
             * Этот метод стоит использовать, когда мы "скользим" по таблице.
             * Пагинация и скролл не меняют фильтров и сортировок, но меняют offset и limit.
             * Зная это мы можем подгружать данные на перед и доставать их из кэша когда надо.
             * @param  {Object}   params   Объект вида:
             * {
             *   offset: {Number},  индекс строки, которую хочет видеть пользователь
             *   limit: {Number}  кол-во строк, которое хочет видеть пользователь
             * }
             * @param  {Function} callback
             * callback(models)
             * models: массив объектов, где каждый объект это запись из таблицы
             * @memberof Cursor.prototype
             */
    _sliceLoad(params, callback) {
        if ( !isFunction(callback) ) {
            callback = function() {};
        }

        let offset = params.offset;
        let limit = params.limit;
        let index = this.firstRowIndex;
        let ids;
        let models;

        if ( limit == "all" ) {
            limit = 9999999999; // норм костыль
        }

        // фиксим поиск слова Китай в выпадающем
        if ( this.count < this.offset ) {
            this.offset = 0;
        }
        offset = Math.max(offset, 0);

        // если курсор подошел к своим границам, то надо обновить буфер
        if (
            offset + limit > index + this.size
                || offset < index
        ) {
            index = offset - this.size / 2;
            index = Math.min(this.count - this.size / 2, index);
            index = Math.max(index, 0);
            params._loadCount = false;
            this._refreshLoad(index, params, callback);
            return;
        }

        // все необходимые данные есть в памяти кэша, достаем их по известным нам id
        ids = this.ids.slice(offset - index, offset - index + limit);
        models = this.storage.collection.getModels(ids);

        if ( params.rightOff ) {
            callback(models);
        } else {
            setTimeout(function() {
                callback(models);
            }, 0);
        }

        return models;
    },

    /**
             * Прикрепляет строку к курсору в указанное место
             * @param {object} id - id модели, которую закрепляем до смены фильтров или сортировки
             * @param {object} params - конфигурация attach
             * @param {number} params.at - позиция в которую будет прикреплена строка, при текущей сортировке и фильтрах.
             * Нумерация от начала таблицы
             * @memberof Cursor.prototype
             */
    attach(params) {
        params = deepMixin({
            id: false,
            at: 0,
            // пример 1  ignoreFilter: false
            //   создаем нового контрагента и гуляем по вкладкам ролей
            //   новый прикрепленный контрагент должен быть только в совей влкадке
            // пример 2  ignoreFilter: true
            //   чат, дважды кликаем на комнату, в которой нет сообщений
            //   в списке текущих диалогов стоит фильтр по has_message,
            //   но комната должна зафиксироватся в начало диалогов
            ignoreFilter: false,

            // если закрепление идет на одну и туже позицию,
            // то возможно потребуется смещение уже прикрепленных строк
            moveOthers: false
        }, params);
        let id = params.id + "";

        if ( !id ) {
            console.error("id: must have");
            return;
        }

        let at = +params.at;
        if ( at < 0 ) {
            console.error("'at' must be greater than zero: ", at);
            return;
        }

        if ( params.moveOthers ) {
            if ( params.at in this.attached ) {
                let newAttached = {};

                for (let at in this.attached) {
                    newAttached[ at + 1 ] = this.attached[ at ];
                }

                this.attached = newAttached;
            }
        }

        this.attached[ params.at ] = params;
        this._refresh = true;
    },

    clearAttached() {
        if ( Object.keys(this.attached).length ) {
            this.attached = {};
            this._refresh = true;
        }
    },

    /**
             * если точно известно, что запрос на обновление данных можно выполнить синхрннно,
             * (изменился offset или limit, но запрос попадает в рамки текущего буфера)
             * то можно использовать этот метод. Он возьмет данные из кэша и вызовет событие на перерисовку
             * @see GridList
             * @memberof Cursor.prototype
             */
    syncSliceRows() {
        let params = this.getParams();
        params.rightOff = true;
        let models = this._sliceLoad(params);

        this.reset();
        models.forEach(function(model, i) {
            this.add(model, { at: i });
        }, this);
        this.trigger("addModels updateRows", models);
    },

    /**
             * Новая строка может не попадать под фильтр курсора,
             * но пользователь ее все равно должен видеть.
             * @param {RowModel} model - новосозданная модель строки в таблице
             * @memberof Cursor.prototype
             */
    addNewModel(model) {
        let index = this.offset - this.firstRowIndex + this.models.length;
        this.ids.splice(index, 0, model.id + "");
        // !!! wtf
        this.splice(index, 0, model.id + "");
        this._byId[model.id] = model;
        this.models.push(model);
        this.count++;
    },

    /**
             * Проходимся по каждой строке,
             * которая есть в таблице и подходит под фильтры.
             * Суть метода в том,
             * что некоторые строки могут быть незагружены в память,
             * более того, каждая строка должна быть подгружена со всеми полями.
             * P.S. смотри AccountTransferForm
             * @param {Function} iteration функция,
         * которая будет вызвана на каждой строке
             * @param {Function} callback функция,
         * которая будет вызвана после прохождения по всем строкам
             * @memberof Cursor.prototype
             */
    eachRow(iteration, callback) {
        if ( !isFunction(iteration) ) {
            iteration = function() {};
        }
        if ( !isFunction(callback) ) {
            callback = function() {};
        }

        // сначала получим список всех id
        let params = deepClone(this.getParams());
        params.offset = 0;
        params.limit = "all";
        params.columns = ["ID"];
        delete params._refresh;

        this.storage.get(params, (err, models) => {
            if ( err || !isArray(models) ) {
                return callback();
            }

            let ids = models.map(function(model) {return model.id;});

            let iterations = ids.map(this._eachRow_loadRow.bind(this, iteration));
            series(iterations, callback);
        });
    },

    // вспомогательная функция для .eachRow
    _eachRow_loadRow(iteration, id) {
        let storage = this.storage;

        return function(next) {
            storage.get(id, function(err, model) {
                if ( !err ) {
                    iteration(model);
                }
                next(err);
            });
        };
    },

    /**
             * Обновляем данные текущего кадра, в него могут попасть новые строки
             * @param {Function} callback
             * @memberof Cursor.prototype
             */
    reload(callback) {
        if ( !isFunction(callback) ) {
            callback = function() {};
        }

        this.load({ cache: false, refresh: true }, callback);
    },

    /**
             * В полном режиме работы над таблицами нужны цвета в таблице,
             * а в выпадающих списках нет
             * @memberof Cursor.prototype
             */
    toggleColors(state) {
        if ( !arguments.length ) {
            state = !this.colors;
        }
        this.colors = !!state;
    },

    /**
             * Если таблица открыта в полном режиме, то добавляем 2 поля:
             * пользователь создавший строку,
             * дата создания строки
             * @memberof Cursor.prototype
             */
    toggleCreateFields(state) {
        if ( !arguments.length ) {
            state = !this._createFields;
        }
        this._createFields = !!state;
    },

    destroy() {
        this.stopListening();
        this._events = {};
    },

    /**
             * для компонента фильтров по столбцу с типом дата необходимо
             * знать список уникальных дат
             * @param {String} field - поле, по которому получаем список дат
             * @param {Function} callback
             * @memberof Cursor.prototype
             */
    getUniqueDays(field, callback) {
        if ( !isFunction(callback) ) {
            callback = function() {};
        }

        let filter = this.filter;
        if ( this.sysFilter ) {
            if ( filter ) {
                filter.add(this.sysFilter.clone());
            } else {
                filter = this.sysFilter.clone();
            }
        }

        if ( this.filter3 ) {
            if ( filter ) {
                filter = this.filter3.and(filter);
            } else {
                filter = this.filter3.clone();
            }
        }

        let scope = this.scope;

        this.storage.getUniqueDays({
            field,
            filter,
            scope
        }, function(err, days) {
            days = days || [];
            callback(days);
        });
    },

    /**
             * Список уникальных значений по столбцу
             * @param {String} field Столбце по которому будет выборка
             * уникальных значений
             * @memberof Cursor.prototype
             */
    distinct(field) {
        this._distinct = field;
        this._refresh = true;
    },

    _prepareModel(attrs, options) {
        // !!! options изменяется по ссылке !!!
        if ( !options ) {
            options = {};
        }
        options.storage = this.storage;

        return Cursor.__super__._prepareModel.call(this, attrs, options);
    },

    refreshModel(model, callback) {
        if ( !isFunction(callback) ) {
            callback = function() {};
        }

        let id = model.id || this.get(model).id;

        // потому что loadRow без $currentUser, а Антон на этот момент химичит с бекендом
        this.storage.refreshGet({
            filter: {
                ID: {
                    type: "equal",
                    key: "ID",
                    value: id
                }
            }
        }, function(err, models) {
            let model = models && models[0];
            callback(err, model);
        });
    },

    setScope(scope) {
        this.scope = scope;
    }
});

let originalExtend = Cursor.extend;
Cursor.extend = function(className, proto) {
    if ( isObject(className) ) {
        proto = className;
        className = "ChildCursor";
    }

    proto = deepMixin({}, proto);

    if ( isObject(proto.model) ) {
        proto.model = RowModel.extend(className + "Model", proto.model);
    }

    if ( isFunction(proto.model) && !(proto.model.prototype instanceof RowModel) ) {
        throw new Error("model_should_inherit_from_RowModel");
    }

    return originalExtend.call(this, className, proto);
};

export default Cursor;
